import { memo } from 'react';
import { Box, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import { NumericFormat } from 'react-number-format';
import { TextColor } from '@setvi/shared/enums';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { SText } from '@setvi/shared/components';
import { RowContainer, FirstRowBox, SecondRowBox } from '../styled';
import {
  NO_FIELD_LOCK,
  type FieldLockState
} from '../../../../pages/details/realtime/use-field-locks';
import { FieldLockIcon, FieldLockAvatar } from '../field-lock-badge';

interface SellColumnProps {
  product: QuoteProductDTO;
  productId: string;
  onSellChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  // This input's own lock — drives the editor avatar shown ON the Unit Price
  // input (only when THIS field is the one being edited).
  sellLock?: FieldLockState;
  // True when any pricing input (Sell / GP$ / GP%) is held by another user: the
  // three lock+disable together, but the avatar only shows on the held one.
  // Both omitted by non-collab consumers (Auto RFQ quote tab) → no locking.
  pricingLockedByOther?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  alwaysEditable?: boolean;
}

const useStyles = makeStyles(() => ({
  input: {
    width: '110px',
    textAlign: 'center',
    borderRadius: '99px',
    border: '1px solid #e2e2ea',
    height: '40px',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#F8F9FA',
    '& input[type=number]': {
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        display: 'none'
      }
    }
  },
  // "Locked by another user" state: soft blue accent instead of dead grey.
  locked: {
    backgroundColor: '#EEF4FF !important',
    borderColor: '#9DC7F2 !important',
    color: '#5D7083 !important',
    WebkitTextFillColor: '#5D7083 !important',
    cursor: 'not-allowed !important'
  },
  // Extra left room for the in-input lock glyph — only when this field shows
  // the glyph (i.e. it's the one being edited), so grouped-but-not-edited
  // pricing inputs don't get an empty gap.
  glyphPad: {
    paddingLeft: '32px !important'
  }
}));

export const SellColumn = memo(
  ({
    product,
    productId,
    onSellChange,
    sellLock = NO_FIELD_LOCK,
    pricingLockedByOther = false,
    disabled = false,
    disabledTooltip = '',
    alwaysEditable = false
  }: SellColumnProps) => {
    const classes = useStyles();
    // `editing` = this Unit Price input is the one held → show the avatar here.
    const { lockedByOther: editing, holder, acquire, release } = sellLock;

    if (product?.isPricingFetching) {
      return (
        <RowContainer>
          <FirstRowBox>
            <Skeleton variant="rect" width={100} height={36} />
          </FirstRowBox>
          <SecondRowBox>
            <Skeleton
              variant="rect"
              width={100}
              height={26}
              style={{ margin: '0 auto' }}
            />
          </SecondRowBox>
        </RowContainer>
      );
    }

    const isInputDisabled =
      pricingLockedByOther ||
      (!alwaysEditable &&
        (disabled || (!product?.isEditable && !product?.isFreeHand)));

    const sellInput = (
      <NumericFormat
        value={product?.sell}
        decimalScale={2}
        fixedDecimalScale
        thousandSeparator=","
        decimalSeparator="."
        prefix="$"
        onChange={e =>
          onSellChange(
            productId,
            product,
            e.target.value.replace(/[^\d.-]/g, '')
          )
        }
        style={{
          width: '100%',
          color: TextColor.Blue,
          fontWeight: 600
        }}
        disabled={isInputDisabled}
        onFocus={acquire}
        onBlur={release}
        className={clsx(
          classes.input,
          pricingLockedByOther && classes.locked,
          editing && classes.glyphPad
        )}
      />
    );

    const sellField = (
      <Box style={{ position: 'relative', display: 'inline-flex' }}>
        {sellInput}
        {editing && (
          <>
            <FieldLockIcon />
            <FieldLockAvatar holder={holder} />
          </>
        )}
      </Box>
    );

    // When locked, the avatar carries the "X is editing" tooltip — don't wrap
    // the input in a second tooltip. Only show the not-editable tooltip.
    const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : '';

    return (
      <RowContainer
        style={{
          justifyContent: 'space-between'
        }}>
        <FirstRowBox>
          {tooltipTitle ? (
            <Tooltip title={tooltipTitle} arrow>
              <Box>{sellField}</Box>
            </Tooltip>
          ) : (
            sellField
          )}
        </FirstRowBox>

        <SecondRowBox>
          <SText
            fontColor="#333D47"
            weight="semibold"
            style={{
              textAlign: 'center',
              width: '100%'
            }}
            title={
              product?.cost > 0
                ? product?.cost.toLocaleString('en-US', {
                    currency: 'USD',
                    style: 'currency'
                  })
                : '-'
            }
            prefix={
              <span
                style={{
                  fontWeight: 'normal'
                }}>{`Cost: `}</span>
            }
          />
        </SecondRowBox>
      </RowContainer>
    );
  }
);

SellColumn.displayName = 'SellColumn';
